// One toast per outgoing message, from the Send click until it is delivered.
//
// The thread bubble only exists on that ticket's page, and the whole point of
// the outbox is that the person can be anywhere. So the toast is what says "this
// is still going out — keep the tab open":
//
//   "Sending in 5s…  [Undo]"      the undo window, counting down
//   "Sending…" / "Uploading 1 of 2 files…"   spinner, until the server answers
//   "Reply sent"                  (rendered by outbox-jobs.ts when it succeeds)
//   "Your reply didn't send  [Retry]"        (ditto, on failure; Retry goes back
//                                             to the spinner)
//
// It is ONE toast the whole way: every call below uses `outboxToastId(job.id)`,
// and sonner updates a toast in place when it is called again with the same id.
// That gives the person a single thing to watch instead of a countdown that
// vanishes and a success that appears from nowhere.
//
// Rules this relies on (sonner 1.7):
//  * An update is `{ ...old, ...new }`, so any field NOT passed is kept — an old
//    Undo button would survive into the spinner. Every call names `action`,
//    `cancel`, `duration` and `dismissible` explicitly.
//  * Dismissing a toast and then updating the same id makes it disappear (it is
//    marked for deletion). So a toast that is about to change state is never
//    dismissed — see `retryJob` and the Undo button, which preventDefault().
//  * A button click closes its toast unless the handler calls preventDefault().
//
// Success and failure toasts are NOT here: they depend on the kind of message
// (where "Open" goes, what Retry means), so the composers pass them in as the
// job's onSuccess / onFailure, using the same id.
//
// Pure: the toast function is injected, so this is testable without Sonner.

import { undoSecondsLeft, type Outbox, type OutboxJobView, type OutboxState } from './send-outbox';

export interface OutboxToastOptions {
  id?: string | number;
  description?: string;
  duration?: number;
  /** false = no swipe / close. The countdown and the spinner are replaced by
   *  the next state, never closed by hand. */
  dismissible?: boolean;
  action?: { label: string; onClick: (event: { preventDefault: () => void }) => void } | undefined;
  cancel?: { label: string; onClick: () => void } | undefined;
}

export interface OutboxToastApi {
  (message: string, data?: OutboxToastOptions): string | number;
  loading: (message: string, data?: OutboxToastOptions) => string | number;
  dismiss: (id?: string | number) => unknown;
}

export interface OutboxToastHooks {
  /** "Edit" on the cancelled-send toast. Receives the held job. */
  onEdit?: (job: OutboxJobView) => void;
}

/** The id of the toast that follows one job through its whole life. */
export const outboxToastId = (jobId: string) => `outbox:${jobId}`;

/** How long "Reply sent" / "Ticket created" stays up. */
export const SENT_TOAST_MS = 4000;

/** Safety net: a job that ends without anyone replacing its spinner (a future
 *  job kind without a success toast) must not leave an un-closable "Sending…"
 *  behind. Long enough for the real success toast (on the same id) to have been
 *  read and to have faded on its own. */
export const SENT_TOAST_GRACE_MS = 6000;

const TICK_MS = 250;

interface Shown {
  state: OutboxState;
  stage: string | null;
  /** The countdown text last shown (queued only), so a tick re-issues the
   *  toast only when the number actually changed. */
  label: string | null;
}

/** Start mirroring `outbox` into toasts. Returns a stop function. */
export function startOutboxToasts(
  outbox: Outbox,
  toast: OutboxToastApi,
  hooks: OutboxToastHooks = {},
): () => void {
  const known = new Map<string, Shown>();
  const graceTimers = new Set<ReturnType<typeof setTimeout>>();
  let ticker: ReturnType<typeof setInterval> | null = null;

  const show = (job: OutboxJobView, prev: Shown | undefined, label: string | null) => {
    const id = outboxToastId(job.id);

    switch (job.state) {
      case 'queued':
        toast(label ?? 'Sending…', {
          id,
          description: job.title,
          // Never times out on its own: the next state replaces it, and a
          // timeout landing at the same moment would delete the toast we are
          // about to update.
          duration: Infinity,
          dismissible: false,
          action: {
            label: 'Undo',
            onClick: (e) => {
              // Keep this toast: it is about to become "Send cancelled".
              e.preventDefault();
              void outbox.undo(job.id);
            },
          },
          cancel: undefined,
        });
        break;

      case 'sending':
        toast.loading(job.stage ?? 'Sending…', {
          id,
          description: `${job.title} — keep this tab open until it's sent`,
          duration: Infinity,
          dismissible: false,
          action: undefined,
          cancel: undefined,
        });
        break;

      case 'held':
        // Only the Undo → held step. (A held job seen cold has nothing to say.)
        if (prev?.state !== 'queued') break;
        toast('Send cancelled — nothing was sent', {
          id,
          description: job.title,
          duration: job.kind === 'compose' ? 15_000 : 4000,
          dismissible: true,
          action:
            job.kind === 'compose' && hooks.onEdit
              ? { label: 'Edit', onClick: () => hooks.onEdit?.(job) }
              : undefined,
          cancel: undefined,
        });
        break;

      // 'sent' and 'failed': outbox-jobs renders them on this same id.
      default:
        break;
    }
  };

  const sync = () => {
    const snapshot = outbox.getSnapshot();
    const present = new Set<string>();
    const now = Date.now();

    for (const job of snapshot) {
      present.add(job.id);
      const prev = known.get(job.id);
      const label =
        job.state === 'queued' ? `Sending in ${Math.max(1, undoSecondsLeft(job, now))}s…` : null;
      if (
        prev &&
        prev.state === job.state &&
        prev.stage === job.stage &&
        prev.label === label
      ) {
        continue;
      }
      known.set(job.id, { state: job.state, stage: job.stage, label });
      show(job, prev, label);
    }

    // Jobs that disappeared.
    for (const [jobId, shown] of known) {
      if (present.has(jobId)) continue;
      known.delete(jobId);
      const id = outboxToastId(jobId);
      if (shown.state === 'sent') {
        // Delivered: outbox-jobs put "Reply sent" on this id and it fades on its
        // own. Only clear it if nobody did.
        const timer = setTimeout(() => {
          graceTimers.delete(timer);
          toast.dismiss(id);
        }, SENT_TOAST_GRACE_MS);
        graceTimers.add(timer);
      } else {
        // Discarded or taken back for editing.
        toast.dismiss(id);
      }
    }

    // Tick only while a countdown is on screen.
    const counting = snapshot.some((j) => j.state === 'queued');
    if (counting && ticker === null) {
      ticker = setInterval(sync, TICK_MS);
    } else if (!counting && ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
  };

  const unsubscribe = outbox.subscribe(sync);
  sync();
  return () => {
    unsubscribe();
    if (ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
    for (const timer of graceTimers) clearTimeout(timer);
    graceTimers.clear();
    for (const [jobId, shown] of known) {
      if (shown.state === 'queued' || shown.state === 'sending' || shown.state === 'held') {
        toast.dismiss(outboxToastId(jobId));
      }
    }
    known.clear();
  };
}
